/**
 * shadcn/ui Badge, restyled to the Alpine tokens.
 *
 * NOTE: there is deliberately no `red` / `amber` / `green` variant here. Traffic
 * lights are not a badge style — they are `<SignalBadge>`, which always renders
 * an icon AND a label so the state never depends on colour alone (WCAG 2.2 AA).
 * Keeping the signal palette out of the generic badge stops an unverified
 * finding from ever picking up a traffic-light look by accident.
 */
import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-0.5 text-[12px] leading-5 font-medium [&_svg]:size-3.5',
  {
    variants: {
      variant: {
        neutral: 'border-border bg-surface text-muted',
        outline: 'border-border-strong bg-white text-ink-2',
        info: 'border-transparent bg-signal-info-soft text-signal-info',
        /** The indicative treatment: dashed border + grey. Never a signal colour. */
        indicative: 'border-dashed border-border-strong bg-surface-2 text-muted',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
)

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { badgeVariants }
